'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Shield, Mail, ArrowRight, RefreshCw, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { getStore, setAuthenticated } from '@/lib/store';
import { toast } from 'sonner';

interface EmailForm {
  email: string;
}

const VALID_EMAIL = 'yodermoore@gmail.com';
const MOCK_OTP = '957822';

export default function LoginOTPScreen() {
  const router = useRouter();

  const [step, setStep] = useState<'email' | 'otp' | 'success'>('email');
  const [emailValue, setEmailValue] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [showOtpHint, setShowOtpHint] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<EmailForm>();

  useEffect(() => {
    // Check if already authenticated
    const store = getStore();
    if (store.isAuthenticated) {
      router.replace('/account-dashboard');
    }
  }, [router]);

  const startCountdown = useCallback(() => {
    setCountdown(60);
    setCanResend(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  async function onEmailSubmit(data: EmailForm) {
    if (data.email.toLowerCase() !== VALID_EMAIL) {
      setError('email', { message: 'No account found with this email address.' });
      return;
    }
    setSendingOtp(true);
    // Backend integration point: POST /api/auth/send-otp { email }
    await new Promise((r) => setTimeout(r, 1200));
    setSendingOtp(false);
    setEmailValue(data.email);
    setStep('otp');
    startCountdown();
    toast.success('OTP sent to your email');
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
  }

  function handleOtpChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = value.slice(-1);
    setOtpDigits(newDigits);
    setOtpError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleOtpKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleOtpPaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtpDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  }

  async function handleVerifyOtp() {
    const entered = otpDigits.join('');
    if (entered.length < 6) {
      setOtpError('Please enter all 6 digits of your OTP.');
      return;
    }
    setVerifying(true);
    // Backend integration point: POST /api/auth/verify-otp { email, otp }
    await new Promise((r) => setTimeout(r, 1400));
    if (entered !== MOCK_OTP) {
      setVerifying(false);
      setOtpError('Invalid OTP. Use the demo code shown below.');
      return;
    }
    setAuthenticated(true);
    setStep('success');
    await new Promise((r) => setTimeout(r, 1200));
    router.push('/account-dashboard');
  }

  async function handleResendOtp() {
    if (!canResend) return;
    setSendingOtp(true);
    // Backend integration point: POST /api/auth/resend-otp { email }
    await new Promise((r) => setTimeout(r, 800));
    setSendingOtp(false);
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
    startCountdown();
    toast.success('New OTP sent to your email');
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }

  function maskEmail(email: string) {
    const [user, domain] = email.split('@');
    if (!domain) return email;
    const visible = user.slice(0, 3);
    return `${visible}${'*'.repeat(Math.max(2, user.length - 3))}@${domain}`;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-navy-gradient flex-col justify-between p-10 xl:p-16 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full border border-white/5" />
        <div className="absolute -top-12 -right-12 w-72 h-72 rounded-full border border-white/5" />
        <div className="absolute bottom-32 -left-24 w-80 h-80 rounded-full border border-white/5" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-gold/5 rounded-full" />

        {/* Top logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={44} />
          <div>
            <span className="text-white font-800 text-xl leading-tight block">BlueCrest</span>
            <span className="text-gold-light text-sm font-500 opacity-80">Private Bank</span>
          </div>
        </div>

        {/* Center content */}
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/20 rounded-full px-4 py-1.5 mb-8">
            <Shield size={13} className="text-gold" />
            <span className="text-gold text-xs font-600 tracking-wide">FDIC Insured · Private Client Division</span>
          </div>

          <h1 className="text-white text-4xl xl:text-5xl font-800 leading-tight mb-6">
            Your wealth,<br />
            <span className="text-gold-light">privately secured.</span>
          </h1>

          <p className="text-white/60 text-base leading-relaxed mb-10">
            BlueCrest Private Bank provides elite financial services with institutional-grade security and personalized wealth management for clients who demand the best.
          </p>

          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Client Assets', value: '$2.4B+' },
              { label: 'Years Serving', value: '47' },
              { label: 'Private Clients', value: '1,200+' },
              { label: 'Uptime', value: '99.99%' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <p className="text-gold-light text-2xl font-800 font-tabular">{stat.value}</p>
                <p className="text-white/50 text-xs font-500 mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="relative z-10">
          <p className="text-white/30 text-xs">
            © 2026 BlueCrest Private Bank, N.A. · Member FDIC · Equal Housing Lender
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <AppLogo size={36} />
            <div>
              <span className="text-primary font-800 text-lg leading-tight block">BlueCrest</span>
              <span className="text-gold text-xs font-500">Private Bank</span>
            </div>
          </div>

          {/* Step: Email */}
          {step === 'email' && (
            <div className="animate-fade-in">
              <div className="mb-8">
                <h2 className="text-2xl font-700 text-foreground mb-2">Welcome back</h2>
                <p className="text-muted-foreground text-sm">
                  Enter your registered email address to receive a secure one-time passcode.
                </p>
              </div>

              <form onSubmit={handleSubmit(onEmailSubmit)} className="space-y-5">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5" htmlFor="email">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      className={`w-full pl-10 pr-4 py-3 rounded-lg border bg-card text-foreground text-sm outline-none transition-all duration-150 focus:border-accent focus:ring-2 focus:ring-accent/20 ${
                        errors.email ? 'border-danger' : 'border-border'
                      }`}
                      {...register('email', {
                        required: 'Email address is required.',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Please enter a valid email address.',
                        },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1.5 text-xs text-danger">{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || sendingOtp}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-lg font-600 text-sm transition-all duration-150 hover:bg-navy-light active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sendingOtp ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>

              {/* Demo credentials hint */}
              <div className="hidden mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs font-600 text-blue-800 mb-2">THE MOORES ACCOUNT</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-blue-700 font-mono">Yodermoore@gmail.com</span>
                </div>
              </div>
            </div>
          )}

          {/* Step: OTP */}
          {step === 'otp' && (
            <div className="animate-fade-in">
              <button
                onClick={() => { setStep('email'); setOtpDigits(['', '', '', '', '', '']); setOtpError(''); }}
                className="flex items-center gap-1.5 text-muted-foreground text-sm mb-6 hover:text-foreground transition-colors"
              >
                <ChevronLeft size={16} />
                Back
              </button>

              <div className="mb-8">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Mail size={22} className="text-primary" />
                </div>
                <h2 className="text-2xl font-700 text-foreground mb-2">Check your email</h2>
                <p className="text-muted-foreground text-sm">
                  We sent a 6-digit verification code to{' '}
                  <span className="font-600 text-foreground">{maskEmail(emailValue)}</span>.
                  It expires in 10 minutes.
                </p>
              </div>

              {/* OTP inputs */}
              <div className="flex gap-2.5 justify-between mb-2" onPaste={handleOtpPaste}>
                {otpDigits.map((digit, i) => (
                  <input
                    key={`otp-${i}`}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className={`otp-input ${digit ? 'filled' : ''} ${otpError ? 'border-danger' : ''}`}
                    aria-label={`OTP digit ${i + 1}`}
                  />
                ))}
              </div>

              {otpError && (
                <p className="text-xs text-danger mb-4">{otpError}</p>
              )}

              {/* OTP hint toggle */}
              <div className="hidden mb-5">
                <button
                  type="button"
                  onClick={() => setShowOtpHint(!showOtpHint)}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showOtpHint ? <EyeOff size={13} /> : <Eye size={13} />}
                  {showOtpHint ? 'Hide OTP Verification Swift' : 'OTP Verification Swift'}
                </button>
                {showOtpHint && (
                  <div className="mt-2 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg">
                    <span className="text-xs text-amber-700 font-500">Demo OTP:</span>
                    <span className="font-mono font-700 text-amber-800 tracking-widest">{MOCK_OTP}</span>
                  </div>
                )}
              </div>

              <button
                onClick={handleVerifyOtp}
                disabled={verifying || otpDigits.join('').length < 6}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-lg font-600 text-sm transition-all duration-150 hover:bg-navy-light active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed mb-4"
              >
                {verifying ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Lock size={16} />
                    Verify & Sign In
                  </>
                )}
              </button>

              {/* Resend */}
              <div className="text-center">
                {canResend ? (
                  <button
                    onClick={handleResendOtp}
                    disabled={sendingOtp}
                    className="text-sm text-accent font-600 hover:underline disabled:opacity-50"
                  >
                    {sendingOtp ? 'Sending...' : 'Resend OTP'}
                  </button>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Resend code in{' '}
                    <span className="font-600 text-foreground font-tabular">{countdown}s</span>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="animate-fade-in text-center">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={32} className="text-success" />
              </div>
              <h2 className="text-2xl font-700 text-foreground mb-2">Verified!</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Identity confirmed. Redirecting to your dashboard...
              </p>
              <div className="flex items-center justify-center gap-1.5">
                <RefreshCw size={14} className="animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Loading your account</span>
              </div>
            </div>
          )}

          {/* Security note */}
          <div className="mt-8 flex items-start gap-2.5 p-3 bg-muted/50 rounded-lg">
            <Shield size={14} className="text-success mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Your connection is encrypted with 256-bit SSL. BlueCrest will never ask for your full password via email or phone.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Missing import fix
function ChevronLeft({ size, className }: { size: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}